import { capitalGlobalProposalsService } from '@/services/proposals/capital-global';
import type {
  CapitalGlobalProposalAttachment,
  CapitalGlobalProposalAttachmentDocumentType,
} from '@/services/proposals/types';
import { s3Service } from '@/services/s3';
import { isInactiveBrokerMessage } from '@/shared/utils/inactive-broker-toast.util';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const capitalGlobalProposalAttachmentsQueryKey = (policyId: string) =>
  ['capital-global-proposal-attachments', policyId] as const;

export function useCapitalGlobalProposalAttachments(policyId: string) {
  const trimmed = policyId.trim();

  return useQuery({
    queryKey: capitalGlobalProposalAttachmentsQueryKey(trimmed),
    enabled: Boolean(trimmed),
    // O Error daqui não carrega status: sem isto o 403 de corretora inativa
    // seria tentado de novo como falha de rede.
    retry: (failureCount, error) =>
      !isInactiveBrokerMessage(error.message) && failureCount < 3,
    queryFn: async (): Promise<CapitalGlobalProposalAttachment[]> => {
      const response =
        await capitalGlobalProposalsService.listAttachments(trimmed);

      if (!response.success) {
        throw new Error(
          response.error || 'Erro ao carregar anexos da proposta'
        );
      }

      return response.data?.items ?? [];
    },
  });
}

export function useRegisterCapitalGlobalProposalAttachment(policyId: string) {
  const queryClient = useQueryClient();
  const trimmed = policyId.trim();

  return useMutation({
    mutationFn: async ({
      documentType,
      file,
    }: {
      documentType: CapitalGlobalProposalAttachmentDocumentType;
      file: File;
    }) => {
      const prefix = `proposals/${trimmed}/${documentType}`;
      const keyPath = await s3Service.uploadFile(file, prefix);

      const response = await capitalGlobalProposalsService.registerAttachment(
        trimmed,
        {
          documentType,
          keyPath,
          fileName: file.name,
        }
      );

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao registrar anexo');
      }

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: capitalGlobalProposalAttachmentsQueryKey(trimmed),
      });
    },
  });
}

export function useSoftDeleteCapitalGlobalProposalAttachment(policyId: string) {
  const queryClient = useQueryClient();
  const trimmed = policyId.trim();

  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const response = await capitalGlobalProposalsService.softDeleteAttachment(
        trimmed,
        attachmentId
      );

      if (!response.success) {
        throw new Error(response.error || 'Erro ao excluir anexo');
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: capitalGlobalProposalAttachmentsQueryKey(trimmed),
      });
    },
  });
}

export function mapAttachmentsByDocumentType(
  items: CapitalGlobalProposalAttachment[]
): Partial<
  Record<
    CapitalGlobalProposalAttachmentDocumentType,
    CapitalGlobalProposalAttachment
  >
> {
  return items.reduce<
    Partial<
      Record<
        CapitalGlobalProposalAttachmentDocumentType,
        CapitalGlobalProposalAttachment
      >
    >
  >((acc, item) => {
    acc[item.documentType] = item;
    return acc;
  }, {});
}
